/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Box, Button, Grid, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { LooseObject, ServiceRecord } from 'api/types';
import { get, isEmpty, isNil, omitBy } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useCopyToClipboard } from 'usehooks-ts';

import InputCopyField from '../../../components/form-elements/InputCopyField';
import { ServiceRecordIcon } from '../../../components/Icons';
import Link from '../../../components/Link';
import { dateTimeTransformer, dateTransformerNoTimezone } from '../../../helpers/dateTransformer';
import { displayRow } from '../../../helpers/general';
import { ContentCard } from '../../../layout/ContentCard';
import { LanguageHighlight } from '../LanguageHighlight';
import ServiceRecordData from '../ServiceRecordData';

const JLR = (props: {
  record: ServiceRecord;
  isLoading: boolean;
  sendRequest(x: object): Promise<void>;
  token: string | undefined;
  schema: any;
  workshopLanguage: string;
  canChangeVin?: boolean;
}) => {
  const [applyAnnimation, setApplyAnnimation] = useState(false);
  const { t } = useTranslation();
  const { isLoading, record, schema, workshopLanguage, canChangeVin } = props;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [value, copy] = useCopyToClipboard();
  const recordData: LooseObject = omitBy(record?.data, (value) => value === undefined || value === null) || {};

  const additionalNotes = get(record, 'additional_notes', '-');

  return (
    <ContentCard
      isLoading={isLoading}
      icon={<ServiceRecordIcon boxSize={10} />}
      minH="fit-content"
      header={`${t('service_record:header')}( ${record?.user_readable_id} )`}
      data-test-id="service-record-detail-card"
      contentDescription={
        <HStack justifyContent={'space-between'}>
          <Link to={`/app/op/workshops/${record?.workshop_id}`}>{t('service_record:workshops.title')}</Link>
          <Text>
            {t('forms:submitted_at.label', {
              date: dateTimeTransformer(get(record, 'submitted_at', '-')),
            })}
          </Text>
        </HStack>
      }
    >
      <Grid w="full" templateColumns="repeat(auto-fit, minmax(340px, 1fr))" gap={4} rowGap={8}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Heading size={'xxs'}>{t('service_record:title')}</Heading>
            <div onClick={() => setApplyAnnimation(true)}>
              <InputCopyField
                copy={{
                  currentCopiedValue: value,
                  handleCopy: copy,
                }}
                field={{
                  value: get(record?.vehicle, 'vin', '-'),
                  label: t('forms:vin.label'),
                }}
                customContent={
                  canChangeVin &&
                  ((record?.status === 500 && record?.status_reason === 501) || (record?.status === 500 && record?.status_reason === 599)) && (
                    <Button colorScheme={'red'} size="sm" as={NavLink} to={`/app/op/service-record/${record?.id}/vin-update`}>
                      {t('service_record:vin.update.button')}
                    </Button>
                  )
                }
                aria-label={'vin'}
              />
            </div>
            {workshopLanguage?.toLocaleLowerCase() !== 'de' && (
              <LanguageHighlight workshopLanguage={workshopLanguage} applyAnnimation={applyAnnimation} />
            )}
            {!isNil(recordData?.inspection_type) &&
              displayRow({
                label: t('forms:inspection_type.jlr.label'),
                content: recordData?.inspection_type ? t('forms:inspection_type.dropdown.' + recordData?.inspection_type) : '-',
                isLoading,
              })}
          </Stack>

          <Stack spacing={2}>
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
              }}
              field={{
                value: get(record, 'workshop_reference', '-'),
                label: t('forms:workshop_reference.label.default'),
              }}
              aria-label={'workshop_reference'}
            />
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: dateTransformerNoTimezone(get(record, 'date', '-'), 'yyyy-MM-dd'),
              }}
              field={{
                value: dateTimeTransformer(get(record, 'date', '-')),
                label: t('forms:service_date.label'),
              }}
              aria-label={'service_date'}
            />
            <InputCopyField
              copy={{
                currentCopiedValue: value,
                handleCopy: copy,
                copyValue: get(record, 'mileage', '-'),
              }}
              field={{
                value: get(record, 'mileage', '-') + ' km',
                label: t('forms:mileage.label'),
              }}
              aria-label={'mileage'}
            />
          </Stack>
        </Stack>
        {!isEmpty(schema) && (
          <Grid gap={4} alignSelf={'start'}>
            <Heading size={'xxs'}>{t('service_record:data')}</Heading>
            <ServiceRecordData recordData={recordData} schema={schema} />
            {!isNil(additionalNotes) && (
              <Box ml="60px">
                {displayRow({
                  label: t('forms:additional_notes.label'),
                  content: additionalNotes,
                  isLoading,
                  contentOnNewLine: true,
                })}
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    </ContentCard>
  );
};

export default JLR;
